<template>
  <div>
    <vue-form-generator
      :schema="schemas.basic.schema"
      :model="model"
      :options="schemas.basic.options"
      ref="basicForm"
      @keyup.enter.native="updateContactInformation"
    />
    <hr />
    <vue-form-generator
      :schema="schemas.phones.schema"
      :model="model"
      :options="schemas.phones.options"
      ref="phoneForm"
      @keyup.enter.native="updateContactInformation"
    />
    <div class="col-md-12 errors help-block" v-if="errors.phones">
      {{ errors.phones }}
    </div>
    <hr />
    <vue-form-generator
      :schema="schemas.opt.schema"
      :model="model"
      :options="schemas.opt.options"
      ref="optForm"
      @keyup.enter.native="updateContactInformation"
    />
    <hr />
    <address-form-fields
      ref="personalAddressForm"
      placeholder="Primary Residence"
      :form="personalAddressModel"
      :showResidenceStatus="true"
      @address-updated="personalAddressModel = $event"
    />
    <b-form-checkbox
      id="contact-same-address"
      v-model="hideMailingAddress"
      @change="toggleSameAddress"
    >
      Use the address above for mailing
    </b-form-checkbox>
    <address-form-fields
      v-if="!hideMailingAddress"
      ref="mailingAddressForm"
      placeholder="Mailing Address"
      :form="mailingAddressModel"
      @address-updated="mailingAddressModel = $event"
    />

    <wizard-verified-submit-button
      v-show="wizard"
      text="Update Personal Information"
      @submit="updateContactInformation"
    />
    <b-row v-show="!wizard" class="text-right">
      <b-col>
        <div class="col-md-12">
          <b-button
            id="target"
            class="btn-anchor raised form-submit-btn"
            @click="updateContactInformation"
          >
            Update Personal Information
          </b-button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>


<script>
import VueFormGenerator from 'vue-form-generator';
import helper from '@/libs/helper';
import AddressFormFields from '@/components/utils/address/AddressFormFields';
import { DEFINE_PROFILE_ADDRESS, DEFINE_PROFILE_CONTACT } from '@/vuex/modules/profile/types/actions';

import PERSONAL_ADDRESS_MODEL from '@/components/profile/sections/tabs/contact/models/personalAddress.json';
import MAILING_ADDRESS_MODEL from '@/components/profile/sections/tabs/contact/models/mailingAddress.json';
import BASIC_SCHEMA from '@/components/profile/sections/tabs/contact/schemas/basic';
import OPT_SCHEMA from '@/components/profile/sections/tabs/contact/schemas/opt';
import PHONES_SCHEMA from '@/components/profile/sections/tabs/contact/schemas/phones';
import WizardVerifiedSubmitButton from '@/components/utils/buttons/form/WizardVerifiedSubmitButton';

export default {
  components: {
    WizardVerifiedSubmitButton,
    AddressFormFields,
    'vue-form-generator': VueFormGenerator.component,
  },
  props: {
    wizard: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      personalAddressModel: helper.copy(PERSONAL_ADDRESS_MODEL),
      mailingAddressModel: helper.copy(MAILING_ADDRESS_MODEL),
      hideMailingAddress: false,
      model: {
        email: '',
      },
      errors: {
        phones: null,
      },
      schemas: {
        basic: BASIC_SCHEMA,
        opt: OPT_SCHEMA,
        phones: PHONES_SCHEMA,
      },
    };
  },
  mounted() {
    const profile = this.$store.getters.profile;
    if (profile && profile.contact) {
      this.model = helper.copy(profile.contact);
      this.model.phones.forEach((phone) => {
        /* eslint no-param-reassign: ["error", { "props": false }] */
        phone.number = helper.phoneMask(phone.number);
      });
    }
  },
  created() {
    const profile = this.$store.getters.profile;
    if (profile && profile.address) {
      this.personalAddressModel = helper.copy(profile.address);
    }
    if (profile && profile.contact.mailingAddress) {
      this.mailingAddressModel = helper.copy(profile.contact.mailingAddress);
      this.mailingAddressModel.sameAddress = false;
    } else {
      this.mailingAddressModel = helper.copy(MAILING_ADDRESS_MODEL);
    }
    this.hideMailingAddress = this.mailingAddressModel.sameAddress;
  },
  methods: {
    isPhonesValid() {
      /*
       * This makes up for the fact that the vfg-array-field has faulty validation.
       * We loop through all of the vfg children of the array field in order to
       * validate each one of them. Then we set an error message if needed.
       */
      let phoneItems = [];
      try {
        phoneItems = this.$refs.phoneForm.$children[0].$children[0].$children;
      } catch (_) {
        /* Ignore if error */
      }
      const phonesValid = phoneItems.map(item => item.$children[0].validate());
      const valid = phonesValid.every(val => val === true);
      const atLeastOne = phoneItems.length !== 0;

      this.errors.phones = !atLeastOne
        ? 'You must register at least one cell phone!'
        : null;
      return valid && atLeastOne;
    },
    isValid() {
      const basicFormValid = this.$refs.basicForm.validate();
      const optFormValid = this.$refs.optForm.validate();
      const phoneFormValid = this.isPhonesValid();
      const personalAddress = this.$refs.personalAddressForm.validate();
      if (this.mailingAddressModel.sameAddress) {
        return (
          basicFormValid && optFormValid && phoneFormValid && personalAddress
        );
      }
      const mailingAddress = this.$refs.mailingAddressForm.validate();
      return (
        basicFormValid &&
        optFormValid &&
        phoneFormValid &&
        personalAddress &&
        mailingAddress
      );
    },
    clearValidationErrors() {
      this.$refs.basicForm.clearValidationErrors();
      this.$refs.optForm.clearValidationErrors();
      this.$refs.personalAddressForm.clearValidationErrors();
      if (!this.mailingAddressModel.sameAddress) {
        this.$refs.mailingAddressForm.clearValidationErrors();
      }
    },
    updateContactInformation() {
      if (!this.isValid()) {
        return;
      }
      Object.assign(this.model, this.personalAddressModel);
      const sameAddress = this.mailingAddressModel.sameAddress;
      const mailingAddressData = helper.copy(this.mailingAddressModel);

      delete mailingAddressData.sameAddress;

      if (!sameAddress) {
        Object.assign(this.model, { mailingAddress: mailingAddressData });
      } else {
        delete this.model.mailingAddress;
        this.mailingAddressModel = helper.copy(MAILING_ADDRESS_MODEL);
      }

      if (!this.model.optIn) this.model.optIn = false;

      this.$reqs
        .post(
          `/users/${this.$store.getters.user.id}/profile/contact`,
          this.model,
        )
        .then((response) => {
          this.$store.dispatch(DEFINE_PROFILE_CONTACT, {
            contact: response.data.contact,
          });
          this.$store.dispatch(DEFINE_PROFILE_ADDRESS, {
            address: response.data.address,
          });
          this.$emit('success', true);
          if (this.wizard) {
            this.$emit('move-to-next-tab', true);
          }
        })
        .catch(() => {});
    },
    toggleSameAddress(value) {
      this.mailingAddressModel.sameAddress = value;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/profile/sections/tabs/contact/ContactForm.vue